import { useCallback, useMemo, useState } from "react";
import moment from "moment-timezone";

moment.tz.setDefault("Europe/Warsaw");
import { Chart } from "react-google-charts";
import { connect } from "react-redux";
import { Select } from "expano-components";
import getGdnChartData from "./const/getGdnChartData";
import getUserStatChartData from "./const/getUserStatChartData";

const LastCompletedChart = ({
  getData,
  handleSetDefaultDate,
  completed_histogram_data,
  user_stats_histogram_data,
  range,
  type,
  packer,
  all_packers,
  status,
  user_role,
}) => {
  const [tab, setTab] = useState("gdns_count");
  const packers_options =
    all_packers?.length > 0
      ? [
          { label: "Wszyscy", value: null },
          ...all_packers?.map((item) => ({
            label: item,
            value: item,
          })),
        ]
      : [{ label: "Wszyscy", value: null }];

  const type_options = [
    {
      label: "Godzinny",
      value: "hour",
      isDisabled: !(range === "yesterday" || range === "today"),
    },
    {
      label: "Dzienny",
      value: "day",
      isDisabled: range === "this_year" || range === "last_year",
    },
    {
      label: "Miesięczny",
      value: "month",
      isDisabled: !(
        range === "current_month" ||
        range === "last_month" ||
        range === "this_year" ||
        range === "last_year"
      ),
    },
  ];

  const range_options = [
    { label: "Wczoraj", value: "yesterday" },
    { label: "Dzisiaj", value: "today" },
    { label: "Ostatnie 7 dni", value: "last_7_days" },
    { label: "Ostatnie 14 dni", value: "last_14_days" },
    { label: "Ostatnie 28 dni", value: "last_28_days" },
    { label: "Ten miesiąc", value: "current_month" },
    { label: "Zeszły miesiąc", value: "last_month" },
    { label: "Ten rok", value: "this_year" },
    { label: "Zeszły rok", value: "last_year" },
  ];

  const tab_options = [
    { label: "Ilość zamówień", value: "gdns_count" },
    { label: "Ilość punktów", value: "points_count" },
  ];

  const data_rows = useMemo(() => {
    if (tab === "gdns_count") {
      return getGdnChartData(completed_histogram_data, range);
    }
    if (tab === "points_count") {
      return getUserStatChartData(user_stats_histogram_data, range);
    }
    return [];
  }, [completed_histogram_data, user_stats_histogram_data, tab, range]);

  const chart_data = useMemo(
    () => [["Element", "Ilość"], ...data_rows],
    [data_rows],
  );

  const handleChartSelect = useCallback(
    ({ chartWrapper }) => {
      const selection = chartWrapper.getChart().getSelection();
      if (!selection.length) return;
      const { row } = selection[0];
      if (row === null) return;

      const [label] = data_rows[row] || [];
      if (!label) return;

      const dateFormat = ["yesterday", "today"].includes(range)
        ? "YYYY/MM/DD HH:mm"
        : "YYYY/MM/DD";

      handleSetDefaultDate(moment.tz(label, dateFormat, "Europe/Warsaw").toDate());
    },
    [data_rows, range, handleSetDefaultDate],
  );

  if (!chart_data) return null;

  return (
    <div className="dashboard">
      <div className="dashboard__header">
        <h2 className="dashboard__title heading">Zamówienia zrealizowane</h2>
        <div style={{ maxWidth: 200 }}>
          <Select
            isDisabled={status === "updating"}
            handleChange={(value) => setTab(value)}
            options={tab_options}
            value={tab_options.find(({ value }) => value === tab)}
          />
        </div>
      </div>
      <div className="chart-box__body">
        <div className="chart-box__selects">
          {user_role === "admin" && (
            <Select
              label="Paker"
              isDisabled={status === "updating"}
              handleChange={(value) =>
                getData({
                  histogram_packer: value,
                  histogram_type: type,
                  histogram_range: range,
                })
              }
              options={packers_options}
              value={packers_options.find(({ value }) => value === packer)}
            />
          )}
          <Select
            label="Typ"
            isDisabled={status === "updating"}
            handleChange={(value) =>
              getData({
                histogram_packer: packer,
                histogram_type: value,
                histogram_range: range,
              })
            }
            options={type_options}
            value={type_options.find(({ value }) => value === type)}
          />
          <Select
            label="Zakres"
            isDisabled={status === "updating"}
            handleChange={(value) =>
              getData({
                histogram_packer: packer,
                histogram_type: type,
                histogram_range: value,
              })
            }
            options={range_options}
            value={range_options.find(({ value }) => value === range)}
          />
        </div>
        <Chart
          key={tab}
          options={{
            colors: ["#5D78FF"],
            chartArea: { width: "85%" },
            vAxis: { minValue: 0, maxValue: 15 },
            hAxis: {
              textStyle: {
                fontSize: 10,
                color: "#595D6E",
              },
            },
            legend: { position: "none" },
          }}
          chartType={tab === "gdns_count" ? "ColumnChart" : "LineChart"}
          loader={null}
          width="100%"
          height="400px"
          data={chart_data}
          chartEvents={[{ eventName: "select", callback: handleChartSelect }]}
        />
      </div>
    </div>
  );
};

export default connect(
  ({ dashboard: { data, status }, current_user: { role } }) => ({
    completed_histogram_data: data?.completed_histogram?.data,
    user_stats_histogram_data: data?.user_stats_histogram?.data,
    range: data?.completed_histogram?.range,
    type: data?.completed_histogram?.type,
    packer: data?.completed_histogram?.packer,
    all_packers: data?.completed_histogram?.all_packers || [],
    status,
    user_role: role,
  }),
)(LastCompletedChart);
